import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="note-autosave"
export default class extends Controller {
  static targets = ["title", "content", "tagList", "form"]
  static values = { 
    noteId: String,
    storageKey: String 
  }

  connect() {
    this.hasUnsavedChanges = false
    this.originalValues = {}
    this.autoSaveTimeout = null
    this.autoSaveDelay = 2000 // 2 seconds delay for auto-save
    
    // Generate storage key based on note ID or 'new' for new notes
    this.storageKeyValue = this.storageKeyValue || `note_draft_${this.noteIdValue || 'new'}`
    
    // Store original values for comparison
    this.storeOriginalValues()
    
    // Restore from localStorage if available
    this.restoreFromStorage()
    
    // Set up event listeners
    this.setupEventListeners()
    
    // Set up beforeunload warning
    this.setupBeforeUnloadWarning()
    
    console.log("Note autosave controller connected")
  }

  disconnect() {
    // Clean up event listeners and timeouts
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }
    
    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    
    console.log("Note autosave controller disconnected")
  }

  storeOriginalValues() {
    this.originalValues = {
      title: this.hasTitleTarget ? this.titleTarget.value : '',
      content: this.hasContentTarget ? this.getContentValue() : '',
      tagList: this.hasTagListTarget ? this.tagListTarget.value : ''
    }
  }

  getContentValue() {
    // Handle both regular textarea and rich text editor
    if (this.contentTarget.tagName.toLowerCase() === 'trix-editor') {
      return this.contentTarget.value || this.contentTarget.innerHTML
    } else {
      return this.contentTarget.value
    }
  }

  setContentValue(value) {
    // Handle both regular textarea and rich text editor
    if (this.contentTarget.tagName.toLowerCase() === 'trix-editor') {
      this.contentTarget.value = value
    } else {
      this.contentTarget.value = value
    }
  }

  getCurrentValues() {
    return {
      title: this.hasTitleTarget ? this.titleTarget.value : '',
      content: this.hasContentTarget ? this.getContentValue() : '',
      tagList: this.hasTagListTarget ? this.tagListTarget.value : ''
    }
  }

  setupEventListeners() {
    // Add input event listeners to all form fields
    if (this.hasTitleTarget) {
      this.titleTarget.addEventListener('input', this.handleInput.bind(this))
    }
    
    if (this.hasContentTarget) {
      // Handle different content input types
      if (this.contentTarget.tagName.toLowerCase() === 'trix-editor') {
        this.contentTarget.addEventListener('trix-change', this.handleInput.bind(this))
      } else {
        this.contentTarget.addEventListener('input', this.handleInput.bind(this))
      }
    }
    
    if (this.hasTagListTarget) {
      this.tagListTarget.addEventListener('input', this.handleInput.bind(this))
    }

    // Handle form submission - clear localStorage when successfully saved
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', this.handleFormSubmit.bind(this))
    }
  }

  setupBeforeUnloadWarning() {
    this.beforeUnloadHandler = (event) => {
      if (this.hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = '您有未保存的更改，确定要离开吗？'
        return '您有未保存的更改，确定要离开吗？'
      }
    }
    
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  handleInput() {
    // Check if values have changed from original
    const currentValues = this.getCurrentValues()
    const hasChanges = this.valuesChanged(this.originalValues, currentValues)
    
    this.hasUnsavedChanges = hasChanges
    
    if (hasChanges) {
      // Clear existing timeout
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout)
      }
      
      // Set new timeout for auto-save
      this.autoSaveTimeout = setTimeout(() => {
        this.autoSave()
      }, this.autoSaveDelay)
      
      // Update visual indicator
      this.updateUnsavedIndicator(true)
    } else {
      this.updateUnsavedIndicator(false)
    }
  }

  valuesChanged(original, current) {
    return original.title !== current.title ||
           original.content !== current.content ||
           original.tagList !== current.tagList
  }

  autoSave() {
    const currentValues = this.getCurrentValues()
    
    // Only save if there's actual content
    if (currentValues.title.trim() || currentValues.content.trim() || currentValues.tagList.trim()) {
      try {
        localStorage.setItem(this.storageKeyValue, JSON.stringify({
          ...currentValues,
          timestamp: new Date().toISOString()
        }))
        
        console.log('Auto-saved to localStorage:', this.storageKeyValue)
        this.showAutoSaveIndicator()
      } catch (error) {
        console.error('Failed to auto-save to localStorage:', error)
      }
    }
  }

  restoreFromStorage() {
    try {
      const saved = localStorage.getItem(this.storageKeyValue)
      if (saved) {
        const data = JSON.parse(saved)
        
        // Only restore if the current form is empty or if user confirms
        const currentValues = this.getCurrentValues()
        const isEmpty = !currentValues.title.trim() && !currentValues.content.trim() && !currentValues.tagList.trim()
        
        if (isEmpty || confirm('检测到未保存的草稿，是否恢复？')) {
          if (this.hasTitleTarget) {
            this.titleTarget.value = data.title || ''
          }
          if (this.hasContentTarget) {
            this.setContentValue(data.content || '')
          }
          if (this.hasTagListTarget) {
            this.tagListTarget.value = data.tagList || ''
          }
          
          // Update the "has changes" status
          this.handleInput()
          
          console.log('Restored from localStorage:', this.storageKeyValue)
        }
      }
    } catch (error) {
      console.error('Failed to restore from localStorage:', error)
    }
  }

  handleFormSubmit() {
    // Clear the unsaved changes flag
    this.hasUnsavedChanges = false
    
    // Clear localStorage after successful save
    setTimeout(() => {
      try {
        localStorage.removeItem(this.storageKeyValue)
        console.log('Cleared localStorage after save:', this.storageKeyValue)
      } catch (error) {
        console.error('Failed to clear localStorage:', error)
      }
    }, 100)
  }

  updateUnsavedIndicator(hasChanges) {
    // Add visual indicator for unsaved changes
    const indicator = this.element.querySelector('.unsaved-indicator')
    if (indicator) {
      indicator.style.display = hasChanges ? 'inline' : 'none'
    } else if (hasChanges) {
      // Create indicator if it doesn't exist
      const newIndicator = document.createElement('span')
      newIndicator.className = 'unsaved-indicator badge bg-warning ms-2'
      newIndicator.textContent = '未保存'
      newIndicator.style.fontSize = '0.75em'
      
      // Try to add it to form header or first label
      const formHeader = this.element.querySelector('h1, h2, h3, h4, h5, h6, .card-header')
      if (formHeader) {
        formHeader.appendChild(newIndicator)
      }
    }
  }

  showAutoSaveIndicator() {
    // Show temporary auto-save indicator
    let indicator = this.element.querySelector('.autosave-indicator')
    if (!indicator) {
      indicator = document.createElement('span')
      indicator.className = 'autosave-indicator badge bg-success ms-2'
      indicator.style.fontSize = '0.75em'
      
      const formHeader = this.element.querySelector('h1, h2, h3, h4, h5, h6, .card-header')
      if (formHeader) {
        formHeader.appendChild(indicator)
      }
    }
    
    indicator.textContent = '已自动保存'
    indicator.style.display = 'inline'
    
    // Hide after 3 seconds
    setTimeout(() => {
      if (indicator) {
        indicator.style.display = 'none'
      }
    }, 3000)
  }

  // Action to manually clear draft
  clearDraft() {
    if (confirm('确定要清除草稿吗？')) {
      try {
        localStorage.removeItem(this.storageKeyValue)
        this.hasUnsavedChanges = false
        this.updateUnsavedIndicator(false)
        console.log('Draft cleared manually')
      } catch (error) {
        console.error('Failed to clear draft:', error)
      }
    }
  }

  // Action to manually save draft
  saveDraft() {
    this.autoSave()
  }
}